import * as React from 'react'

import { Kbd } from '@/components/ui/kbd'
import {
  type ShortcutCombo,
  type ShortcutKey,
  getShortcutCombos,
  getShortcutPlatform,
  getShortcutTokenLabel,
} from '@/lib/keyboard/keyboard-shortcuts'
import type { ShortcutPlatform } from '@/lib/keyboard/types/shortcut-platform'
import { cn } from '@/utils/index'

export interface ShortcutKbdProps extends React.HTMLAttributes<HTMLSpanElement> {
  shortcutKey: ShortcutKey
  comboIndex?: number
  platform?: ShortcutPlatform
  /** Passed to each `Kbd` chip (e.g. primary tooltip surface). */
  chipClassName?: string
  /**
   * When true, shortcut chips are removed from the accessibility tree (e.g. inside `role="option"`
   * rows so the option name is only the primary label, not key names read from nested `<kbd>`).
   */
  ariaHidden?: boolean
}

export function ShortcutKbd({
  shortcutKey,
  comboIndex = 0,
  platform,
  className,
  chipClassName,
  ariaHidden,
  ...props
}: ShortcutKbdProps) {
  const resolvedPlatform = useShortcutPlatform(platform)
  const combos = getShortcutCombos(shortcutKey, resolvedPlatform)
  const combo = combos[Math.min(comboIndex, combos.length - 1)]

  return (
    <ShortcutComboKbd
      combo={combo}
      platform={resolvedPlatform}
      className={className}
      chipClassName={chipClassName}
      ariaHidden={ariaHidden}
      {...props}
    />
  )
}

/** Same chips as `ShortcutKbd`, for rows that use `justify-between` (no `ml-auto` / extra left pad on the cluster). */
export function MenuRowShortcutKbd({ className, ...props }: ShortcutKbdProps) {
  return <ShortcutKbd className={cn('ml-0 shrink-0 gap-0.5 pl-0', className)} {...props} />
}

export interface ShortcutComboKbdProps extends React.HTMLAttributes<HTMLSpanElement> {
  combo: ShortcutCombo
  platform?: ShortcutPlatform
  chipClassName?: string
  ariaHidden?: boolean
}

export function ShortcutComboKbd({
  combo,
  platform,
  className,
  chipClassName,
  ariaHidden,
  ...props
}: ShortcutComboKbdProps) {
  const resolvedPlatform = useShortcutPlatform(platform)
  const shortcutDescription = combo
    .map((token) => getShortcutTokenLabel(token, resolvedPlatform))
    .join(' ')

  return (
    <span
      className={cn(
        'ml-auto inline-flex items-center gap-0.5 pl-4 text-muted-foreground',
        className
      )}
      aria-hidden={ariaHidden ? true : undefined}
      aria-label={ariaHidden ? undefined : shortcutDescription}
      {...props}
    >
      {combo.map((token, index) => (
        <Kbd key={`${token}-${index}`} className={chipClassName}>
          {getShortcutTokenLabel(token, resolvedPlatform)}
        </Kbd>
      ))}
    </span>
  )
}

function useShortcutPlatform(platform?: ShortcutPlatform): ShortcutPlatform {
  const [detectedPlatform, setDetectedPlatform] = React.useState<ShortcutPlatform>(
    () => platform ?? getShortcutPlatform()
  )

  React.useEffect(() => {
    if (platform) {
      setDetectedPlatform(platform)
      return
    }

    setDetectedPlatform(getShortcutPlatform())
  }, [platform])

  return detectedPlatform
}
